import { GridSize } from "@material-ui/core/Grid";
import { QuestionnaireType } from "./questionnaire";
export type AvailableQuestionsLangs = string;

export enum QuestionType {
  FreeText = "FREE_TEXT",
  Radio = "RADIO_BUTTON",
  DropDown = "DROP_DOWN",
  Scale = "SCALE", // Deprecated
  NumericScale = "NUMERIC_SCALE",
  TextScale = "TEXT_SCALE",
  LabelGroup = "LABEL_GROUP",
  RadioFreeText = "RADIO_FREE_TEXT",
  FinalRating = "SCALE_FINAL_RATING"
}

export const questionType = {
  [QuestionType.FreeText]: "freeText",
  [QuestionType.Radio]: "radio",
  [QuestionType.DropDown]: "dropDown",
  [QuestionType.LabelGroup]: "labelGroup",
  [QuestionType.Scale]: "scale", // Deprecated
  [QuestionType.RadioFreeText]: "radioFreeText",
  [QuestionType.FinalRating]: "finalRating",
  [QuestionType.NumericScale]: "numericScale",
  [QuestionType.TextScale]: "textScale"
};

export interface IFreeTextContent {
  text: string;
  placeholder?: string;
  options?: any;
}

// used by radio, dropdown and scale questions
export interface IOptionsContent extends IFreeTextContent {
  options: string[];
}

// options for label group question
export interface ILabelGroupOptions {
  question: string;
  value: string;
}

// used by label group questions
export interface ILabelGroupContent extends IFreeTextContent {
  options: ILabelGroupOptions[];
}

export type QuestionContent = IOptionsContent | ILabelGroupOptions | ILabelGroupContent;

export type FreeTextTranslations = Record<AvailableQuestionsLangs, IFreeTextContent>;
export type OptionsTranslations = Record<AvailableQuestionsLangs, IOptionsContent>;
export type LabelGroupTranslations = Record<AvailableQuestionsLangs, ILabelGroupContent>;

export type QuestionTranslations = FreeTextTranslations | OptionsTranslations | LabelGroupTranslations;

export interface IQuestionTranslationsPayload {
  languageCode: AvailableQuestionsLangs;
  value: IFreeTextContent | OptionsTranslations | LabelGroupTranslations;
}

// it's extended by ITemplateQuestion and IQuestion
interface IQuestionBase {
  id?: string;
  optional: boolean;
  type: QuestionType;
}

export interface ITemplateFreeTextQuestion extends IQuestionBase {
  type: QuestionType.FreeText;
  translations: FreeTextTranslations;
}

export interface ITemplateRadioQuestion extends IQuestionBase {
  type: QuestionType.Radio;
  translations: OptionsTranslations;
}

export interface ITemplateDropDownQuestion extends IQuestionBase {
  type: QuestionType.DropDown;
  translations: OptionsTranslations;
}

export interface ITemplateScaleQuestion extends IQuestionBase {
  type: QuestionType.Scale | QuestionType.NumericScale | QuestionType.TextScale | QuestionType.FinalRating;
  translations: OptionsTranslations;
}

export interface ITemplateFinalRatingQuestion extends IQuestionBase {
  type: QuestionType.FinalRating;
  translations: OptionsTranslations;
}

export interface ITemplateLabelGroupQuestion extends IQuestionBase {
  type: QuestionType.LabelGroup;
  translations: LabelGroupTranslations;
}

export interface ITemplateRadioFreeTextQuestion extends IQuestionBase {
  type: QuestionType.RadioFreeText;
  translations: LabelGroupTranslations;
}

export type TemplateQuestion =
  | ITemplateFreeTextQuestion
  | ITemplateRadioQuestion
  | ITemplateDropDownQuestion
  | ITemplateScaleQuestion
  | ITemplateLabelGroupQuestion
  | ITemplateRadioFreeTextQuestion
  | ITemplateFinalRatingQuestion;

export interface ITemplateQuestionPayload extends IQuestionBase {
  translations: IQuestionTranslationsPayload[];
}

export interface IOptionsResponse {
  value: number; // option number
}

export interface IFreeTextResponse {
  value: string;
}

export interface ILabelGroupResponse {
  category: QuestionCategory;
  value: string;
}

export interface IRadioFreeTextResponse {
  category: number;
  value: string;
}

export type QuestionResponse = IOptionsResponse | IFreeTextResponse | ILabelGroupResponse;

export interface IFreeTextQuestion extends IQuestionBase {
  type: QuestionType.FreeText;
  content: IFreeTextContent;
  response: null | IFreeTextResponse;
}

export interface IRadioQuestion extends IQuestionBase {
  type: QuestionType.Radio;
  content: IOptionsContent;
  response: null | IOptionsResponse;
}

export interface IDrowDownQuestion extends IQuestionBase {
  type: QuestionType.DropDown;
  content: IOptionsContent;
  response: null | IOptionsResponse;
}

export interface IScaleQuestion extends IQuestionBase {
  type:
    | QuestionType.Scale // Will be removed
    | QuestionType.NumericScale
    | QuestionType.TextScale;
  content: IOptionsContent;
  response: null | IOptionsResponse;
}

export interface IFinalRatingQuestion extends IQuestionBase {
  type: QuestionType.FinalRating;
  content: IOptionsContent;
  response: null | IOptionsResponse;
}

export interface ILabelGroupQuestion extends IQuestionBase {
  type: QuestionType.LabelGroup;
  content: ILabelGroupContent;
  response: null | ILabelGroupResponse;
}

export interface IRadioFreeTextQuestion extends IQuestionBase {
  type: QuestionType.RadioFreeText;
  content: ILabelGroupContent;
  response: null | IRadioFreeTextResponse;
}

export type Question =
  | IFreeTextQuestion
  | IRadioQuestion
  | IDrowDownQuestion
  | IScaleQuestion
  | ILabelGroupQuestion
  | IRadioFreeTextQuestion
  | IFinalRatingQuestion;

export interface IElementConfig {
  size: GridSize;
}

export interface IElementTranslations {
  text: string;
}

export interface IElementLabel {
  text: string;
}

export interface IElement {
  id: string;
  question: null | Question;
  config: null | IElementConfig;
  label: null | IElementLabel;
}

// response from BE, must be mapped to IElement
export interface IElementPayload {
  id: string;
  question: Question;
  config: string | null;
  label: null | IElementLabel;
}

export interface ITemplateElement {
  id?: string;
  question: null | TemplateQuestion;
  config: IElementConfig | null;
  translations: null | Record<AvailableQuestionsLangs, IElementTranslations>;
  locked: boolean;
}

// response from BE, must be mapped to ITemplateElement
export interface ITemplateElementPayload {
  id?: string;
  question: null | ITemplateQuestionPayload;
  config: IElementConfig | null;
  locked: boolean;
  translations: null | IQuestionTranslationsPayload[]; // *** can be null!!! ***
}

export interface ITemplateConfig {
  supportedLanguages: AvailableQuestionsLangs[];
}

export interface ITemplate {
  id?: string;
  name: string;
  type: QuestionnaireType;
  config: ITemplateConfig;
  elements: ITemplateElement[];
  submitted?: boolean;
}

export interface ITemplatePayload {
  id?: string;
  name: string;
  type: QuestionnaireType;
  config: ITemplateConfig;
  elements: ITemplateElementPayload[];
}

// Submit template question to BE
export interface ITemplateQuestionSubmit {
  type: QuestionType;
  optional: boolean;
  translations: IQuestionTranslationsPayload[];
}

// Submit template element to BE
export interface ITemplateElementSubmit {
  id?: string;
  question: null | ITemplateQuestionSubmit;
  config: string; // *** stringified json!!! ***
}

// Submit template to BE
export interface ITemplateSubmit {
  name: string;
  type: QuestionnaireType;
  config: string; // *** stringified json!!! ***
  elements: ITemplateElementSubmit[];
}

export enum QuestionCategory {
  supportNeeded = 1,
  strength = 0
}

export const questionCategory = {
  [QuestionCategory.supportNeeded]: "supportNeeded",
  [QuestionCategory.strength]: "strength"
};
